import { moveToFulfilled } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { DeleteTeamsMeetingFulfilledAction } from '../../domains/deleteTeamsMeeting/actions.types';
import type {
  TeamsMeetingsByOrganizerFulfilledAction,
  TeamsMeetingsByOrganizerPendingAction,
} from '../../domains/teamsMeetingsByOrganizer/actions.types';

import type { TeamsMeetingsByOrganizer, TeamsMeetingsByOrganizers } from './types';

const initialState: TeamsMeetingsByOrganizers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

export type TeamsMeetingsByOrganizerAction =
  | TeamsMeetingsByOrganizerFulfilledAction
  | TeamsMeetingsByOrganizerPendingAction
  | DeleteTeamsMeetingFulfilledAction;

const addTeamsMeetingIds = (state: TeamsMeetingsByOrganizers, action: TeamsMeetingsByOrganizerFulfilledAction) => {
  const {
    entities: { teamsMeetingsByOrganizers },
    teamsMeetingsByOrganizersKey,
  } = action.payload;

  const newTeamsMeetingIds = teamsMeetingsByOrganizers[teamsMeetingsByOrganizersKey].teamsMeetingIds;
  const existingTeamsMeetingIds = (
    Object.prototype.hasOwnProperty.call(state.fulfilled.byId, teamsMeetingsByOrganizersKey)
      ? state.fulfilled.byId[teamsMeetingsByOrganizersKey].teamsMeetingIds
      : []
  ).filter((id) => !newTeamsMeetingIds.includes(id));
  const mergedIds = [...existingTeamsMeetingIds, ...newTeamsMeetingIds];

  return moveToFulfilled(state, {
    [teamsMeetingsByOrganizersKey]: {
      ...teamsMeetingsByOrganizers[teamsMeetingsByOrganizersKey],
      teamsMeetingIds: mergedIds,
    },
  });
};

const removeTeamsMeetingId = (state: TeamsMeetingsByOrganizers, teamsMeetingId: string) => {
  const teamsMeetingsByOrganizers = state.fulfilled.byId;

  const filteredTeamsMeetingsByOrganizers = Object.keys(teamsMeetingsByOrganizers).reduce(
    (filtered: TeamsMeetingsByOrganizers['fulfilled']['byId'], id: TeamsMeetingsByOrganizer['id']) => {
      const currentTeamsMeetingIds = state.fulfilled.byId[id].teamsMeetingIds;
      const teamsMeetingIds = currentTeamsMeetingIds.filter((id) => id !== teamsMeetingId);

      return {
        ...filtered,
        [id]: { ...teamsMeetingsByOrganizers[id], teamsMeetingIds },
      };
    },
    {}
  );

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      byId: filteredTeamsMeetingsByOrganizers,
    },
  };
};

const reducer: Reducer<TeamsMeetingsByOrganizers, TeamsMeetingsByOrganizerAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TEAMS_MEETINGS_BY_ORGANIZER.LOAD_FULFILLED':
      return addTeamsMeetingIds(state, action);
    case 'TEAMS_MEETINGS_BY_ORGANIZER.LOAD_PENDING':
      return {
        ...state,
        pending: { byId: { ...state.pending.byId, [action.payload.teamsMeetingsByOrganizersKey]: true } },
      };
    case 'DELETE_TEAMS_MEETING.FULFILLED':
      return removeTeamsMeetingId(state, action.payload.id);
    default:
      return state;
  }
};

export default reducer;
